import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Smile } from 'lucide-react';
import { cn } from '@/lib/utils';

// Design system Gens (07/10/2026): botão discreto da barra do composer (apoio → tinta no hover) e
// células de emoji com hover lima névoa. cn() permite a quem usa ajustar o tamanho sem conflito de classes.

const EMOJIS = [
  '😀','😃','😄','😁','😅','😂','🤣','😊','😇','🙂','😉','😌','😍','🥰','😘','😗','😋','😛','😜','🤪',
  '🤗','🤔','🤩','😎','🥳','😏','😒','😞','😔','😟','😕','🙁','😣','😖','😫','😩','🥺','😢','😭','😤',
  '😡','🤬','😈','👿','💀','☠️','💩','👍','👎','👏','🙌','🤝','💪','🫶','✌️','🤞','🤟','👋','🤚','🖐️',
  '✋','🖖','👌','🤏','✍️','🙏','💅','🎉','🎊','🎂','🔥','⭐','❤️','🧡','💛','💚','💙','💜','🖤','🤍',
  '💔','❣️','💕','💖','💗','💘','💝','💟','✅','❌','⚠️','🚨','💡','📌','📎','🏠','🏥','💊','💉','🩺',
  '📱','💻','⏰','📅','📋','📝','💰','💳','🚗','✈️','🌍','🌈','☀️','🌙','⚡','☁️','❄️','🌊','🍀','🌸',
  '🎯','🏆','🥇','🎖️','🏅','📢','🔔','💬','🗨️','🗯️','💭','📧','📨','📩','📤','📥','💌','✉️','🕐','🕑',
  '🕒','🕓','🕔','🕕','🕖','🕗','🕘','🕙','🕚','🕛','🕜','🕝','🕞','🕟','🕠','🕡','🕢','🕣','🕤','🕥',
  '🕦','🕧','😷','🤒','🤕','🤧','🥵','🥶','😵','🤯','🫠','😶‍🌫️','🫡','🫢','🫣','🤭','🫤','😮‍💨','😤','🤐',
];

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  className?: string;
}

interface GradeDeEmojisProps {
  onSelect: (emoji: string) => void;
}

/**
 * Grade reutilizável para o menu compacto do chat no celular,
 * sem o botão extra que ocuparia a linha do campo de mensagem.
 */
export const GradeDeEmojis = ({ onSelect }: GradeDeEmojisProps) => {
  return (
    <div className="grid grid-cols-8 gap-1 max-h-[200px] overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
      {EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onSelect(emoji)}
          className="flex h-11 w-11 items-center justify-center rounded-lg text-lg transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
        >
          {emoji}
        </button>
      ))}
    </div>
  );
};

export const EmojiPicker: React.FC<EmojiPickerProps> = ({ onSelect, className }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [filter, setFilter] = React.useState('');

  const filtered = filter
    ? EMOJIS.filter((e) => e.includes(filter) || e === filter)
    : EMOJIS;

  const handleSelect = (emoji: string) => {
    onSelect(emoji);
    setIsOpen(false);
    setFilter('');
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-[background-color,color,transform] duration-150 hover:bg-muted hover:text-foreground active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:active:scale-100',
            className,
          )}
          aria-label="Inserir emoji"
        >
          <Smile className="h-[18px] w-[18px]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] max-w-[calc(100vw-2rem)] rounded-2xl border-border p-3 shadow-lg" align="start" side="top">
        <div className="grid grid-cols-10 gap-1 max-h-[200px] overflow-y-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {filtered.map((emoji) => (
            <button
              key={emoji}
              type="button"
              onClick={() => handleSelect(emoji)}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-lg transition-colors hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
            >
              {emoji}
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-10 py-4 text-center text-sm text-muted-foreground">
              Nenhum emoji encontrado
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
